import { zodResolver } from "@hookform/resolvers/zod";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { api, ApiError } from "../../shared/api/client";
import { Button, Drawer, Field, InlineAlert, Input, Select } from "../../shared/ui";
import type {
  CalendarDetail,
  EmployeeDetail,
  EmployeeSummary,
  PageResponse,
} from "../../shared/types/api";

const schema = z.object({
  fullName: z.string().min(1, "Enter their name").max(200),
  phone: z.string().max(32).optional(),
  designation: z.string().min(1, "Enter a designation").max(100),
  department: z.string().min(1, "Enter a department").max(100),
  timeZone: z.string().max(64).optional(),
  reportingManagerId: z.string().optional(),
  workingCalendarId: z.string().optional(),
});

type FormValues = z.infer<typeof schema>;

/**
 * Edits the profile fields of an existing employee.
 *
 * <p>Deliberately narrow. Employment status and roles have their own panels and their own
 * permissions, so someone allowed to correct a phone number cannot reactivate a departed
 * colleague or grant themselves a role by way of this form.
 *
 * <p>The record's version is sent back with the change. If someone else saved first the server
 * answers 409 and this panel says so, rather than silently discarding their edit.
 */
export function EmployeeEditDrawer({
  employee,
  onClose,
  onSaved,
}: {
  employee: EmployeeDetail | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [formError, setFormError] = useState<string | null>(null);

  const managers = useQuery({
    queryKey: ["employees", "managers"],
    queryFn: () =>
      api.get<PageResponse<EmployeeSummary>>(
        "/api/v1/employees?active=true&size=200&sort=fullName,asc",
      ),
    enabled: Boolean(employee),
  });

  const calendars = useQuery({
    queryKey: ["calendars"],
    queryFn: () => api.get<CalendarDetail[]>("/api/v1/working-calendars"),
    enabled: Boolean(employee),
  });

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({ resolver: zodResolver(schema) });

  // Re-seeded whenever a different employee is opened, so the form never shows the previous
  // person's details for the instant before it updates.
  useEffect(() => {
    if (employee) {
      reset({
        fullName: employee.fullName,
        phone: employee.phone ?? "",
        designation: employee.designation,
        department: employee.department,
        timeZone: employee.timeZone,
        reportingManagerId: employee.reportingManagerId ?? "",
        workingCalendarId: employee.workingCalendarId,
      });
    }
  }, [employee, reset]);

  const close = () => {
    setFormError(null);
    onClose();
  };

  const onSubmit = handleSubmit(async (values) => {
    if (!employee) {
      return;
    }
    setFormError(null);
    try {
      await api.patch(`/api/v1/employees/${employee.id}`, {
        ...values,
        reportingManagerId: values.reportingManagerId || undefined,
        workingCalendarId: values.workingCalendarId || undefined,
        version: employee.version,
      });
      onSaved();
      close();
    } catch (error) {
      setFormError(
        error instanceof ApiError && error.isConcurrentModification
          ? "Someone else changed this record while you were editing. Close this panel and reopen it to see their version."
          : error instanceof ApiError
            ? error.message
            : "Could not save the changes.",
      );
    }
  });

  return (
    <Drawer
      open={Boolean(employee)}
      onClose={close}
      title="Edit employee"
      subtitle={employee?.employeeCode}
      width="lg"
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={close}>
            Cancel
          </Button>
          <Button onClick={() => void onSubmit()} loading={isSubmitting}>
            {isSubmitting ? "Saving…" : "Save changes"}
          </Button>
        </div>
      }
    >
      <form onSubmit={onSubmit} noValidate className="space-y-4">
        {formError && <InlineAlert tone="error">{formError}</InlineAlert>}

        <InlineAlert tone="info">
          Employee code and email cannot be changed here — they identify the person across every
          historical record.
        </InlineAlert>

        <Field label="Full name" required error={errors.fullName?.message}>
          <Input autoFocus {...register("fullName")} />
        </Field>

        <div className="grid grid-cols-2 gap-4">
          <Field label="Designation" required error={errors.designation?.message}>
            <Input {...register("designation")} />
          </Field>
          <Field label="Department" required error={errors.department?.message}>
            <Input {...register("department")} />
          </Field>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <Field label="Phone" hint="Optional">
            <Input {...register("phone")} />
          </Field>
          <Field label="Time zone">
            <Input placeholder="Asia/Kolkata" {...register("timeZone")} />
          </Field>
        </div>

        <Field label="Reporting manager" hint="Leave blank for none">
          <Select {...register("reportingManagerId")} disabled={managers.isPending}>
            <option value="">No manager</option>
            {(managers.data?.content ?? [])
              .filter((candidate) => candidate.id !== employee?.id)
              .map((manager) => (
                <option key={manager.id} value={manager.id}>
                  {manager.fullName} ({manager.employeeCode})
                </option>
              ))}
          </Select>
        </Field>

        <Field
          label="Working calendar"
          hint="Decides their expected hours and which holidays apply."
        >
          <Select {...register("workingCalendarId")} disabled={calendars.isPending}>
            {(calendars.data ?? []).map((calendar) => (
              <option key={calendar.id} value={calendar.id}>
                {calendar.name}
              </option>
            ))}
          </Select>
        </Field>
      </form>
    </Drawer>
  );
}
